import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Info, Sparkles, Shield, Gift, ImageIcon, ShoppingBag, SlidersHorizontal, Upload, X } from 'lucide-react';
import { Tooltip } from '../../utils/Tooltip/Tooltip';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { Toggle } from '../ui/Toggle';
import { ProductVariantsConfigurator } from '../components/ProductVariantsConfigurator';
import { axiosInstanceConversationalEngine } from '../../../services/instance';
import Loader from '../../common/Loader';
import { dictionary } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';
import { VisualStyle } from '../../../types/onboarding';
import { Country, Currency, getCountriesList } from '../../../const/countryList';
import { getCurrencySymbol } from '../../../utils/formatters';
import { fileToPngDataUrl } from '../../../utils/imageDataUrl';
import type { PreviousProductSnapshot } from '../../../types/onboarding';

const normalizeComparablePrice = (value?: string | number | null) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : '';
};

const hasPreviousProductOfferChange = (
  snapshot: PreviousProductSnapshot | null | undefined,
  price: string,
  countryCode: string,
  currencyCode: string
) => {
  if (!snapshot) return false;
  return normalizeComparablePrice(snapshot.price) !== normalizeComparablePrice(price) ||
    (snapshot.countryCode ?? '').toUpperCase() !== countryCode.toUpperCase() ||
    (snapshot.currencyCode ?? '').toUpperCase() !== currencyCode.toUpperCase();
};

const previousProductOfferMessage = "Este será el precio de oferta de este embudo; el producto original se mantiene igual.";

export function StepBrandAndPrice() {
  const {
    data,
    updateVisualStyle,
    updateProductData,
    updateAdditionalOffers,
    priceErrors,
    setPriceErrors,
    isFetchingTemplates,
    isFetchingAdTemplates,
    sectionTemplates,
    adTemplates,
    fetchSectionTemplates,
    fetchAdTemplates,
    rankOnboardingTemplates,
  } = useOnboardingStore();
  const { visualStyle, productData } = data;
  const { language, country } = useAuth();
  const countryOptions = useMemo(() => getCountriesList(language), [language]);
  const accountCountry = productData.countryCode || country || Country.COLOMBIA;
  const accountCurrency = countryOptions.find(option => option.country === accountCountry)?.currency || productData.currencyCode || Currency.COP;
  const currencySymbol = getCurrencySymbol(accountCurrency);
  const [brandName, setBrandName] = useState(visualStyle.brandName || '');
  const [mainPrice, setMainPrice] = useState(productData.price || '');
  const [bumpOfferPrice, setBumpOfferPrice] = useState(data.additionalOffers.bumpOffer?.price || '');
  const [warrantyEnabled, setWarrantyEnabled] = useState(productData.warranty !== 'none');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);
  const [brandNameSuggestions, setBrandNameSuggestions] = useState<string[]>([]);
  const [currentSuggestionIndex, setCurrentSuggestionIndex] = useState(0);
  const [brandGenerationError, setBrandGenerationError] = useState<string | null>(null);
  const [isVariationsPanelOpen, setIsVariationsPanelOpen] = useState(false);
  const previewLogo =
    visualStyle.logoImage ||
    visualStyle.brandContext?.logo_dark_url ||
    visualStyle.brandContext?.logo_url ||
    visualStyle.brandContext?.logo_light_url ||
    '';

  const brandErrorMessage =
    language === 'en'
      ? "We couldn't generate brand names. Try again."
      : language === 'pt'
        ? "Nao conseguimos gerar nomes de marca. Tente novamente."
        : "No pudimos generar nombres de marca. Intenta de nuevo.";

  const missingProductContextMessage =
    language === 'en'
      ? "Select or complete a product before generating a brand name."
      : language === 'pt'
        ? "Selecione ou complete um produto antes de gerar um nome de marca."
        : "Selecciona o completa un producto antes de generar un nombre de marca.";

  const fetchSuggestedBrandNames = async () => {
    try {
      const productName = data.productData?.name?.trim() || "";
      const productDescription = data.productData?.details?.trim() || "";

      if (!productName || !productDescription) {
        setBrandGenerationError(missingProductContextMessage);
        return [];
      }

      const response = await axiosInstanceConversationalEngine.post('/brand-suggestions/generate', {
        product_name: productName.split(' ').slice(0, 5).join(' '),
        product_description: productDescription,
        language,

      });

      if (response.data.brand_names && response.data.brand_names.length > 0) {
        setBrandGenerationError(null);
        return response.data.brand_names;
      }

      setBrandGenerationError(brandErrorMessage);
      return [];
    } catch (error) {
      console.error("Error fetching brand names:", error);
      setBrandGenerationError(brandErrorMessage);
      return [];
    }
  };


  const loadBrandNames = async () => {
    if (isGenerating) return;

    setIsGenerating(true);

    try {
      const suggestions = await fetchSuggestedBrandNames();
      setBrandNameSuggestions(suggestions);

      if (suggestions.length === 0) {
        return;
      }

      const updates: Partial<VisualStyle> = {};

      if (suggestions.length > 0) {
        updates.brandName = suggestions[0];
        updates.brandId = null;
        updates.brandContext = null;
        setBrandName(suggestions[0]);
      }

      if (visualStyle.colorPalette === 'custom') {
        updates.colorPalette = 'professional-blue';
      }

      if (Object.keys(updates).length > 0) {
        updateVisualStyle(updates);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateBrandName = () => {
    if (isGenerating) {
      return;
    }

    if (brandNameSuggestions.length === 0) {
      return loadBrandNames();
    }

    setIsGenerating(true);

    try {
      const nextIndex = (currentSuggestionIndex + 1) % brandNameSuggestions.length;
      const newName = brandNameSuggestions[nextIndex];

      setBrandGenerationError(null);
      setBrandName(newName);
      updateVisualStyle({
        brandName: newName,
        brandId: null,
        brandContext: null
      });

      setCurrentSuggestionIndex(nextIndex);
    } catch (error) {
      console.error("Error cycling brand name:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBrandNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBrandGenerationError(null);
    setBrandName(value);
    updateVisualStyle({
      brandName: value,
      ...(visualStyle.brandId && visualStyle.brandContext?.name !== value
        ? { brandId: null, brandContext: null }
        : {})
    });
  };

  const handleLogoUpload = async (file?: File) => {
    if (!file) return;
    try {
      const png = await fileToPngDataUrl(file);
      updateVisualStyle({
        brandName,
        logoImage: png,
        brandId: null,
        brandContext: null,
      });
      setBrandGenerationError(null);
    } catch (error) {
      console.error('Error processing brand logo:', error);
      setBrandGenerationError(
        language === 'en'
          ? 'We could not process the logo.'
          : language === 'pt'
            ? 'Nao conseguimos processar o logo.'
            : 'No pudimos procesar el logo.'
      );
    }
  };

  const handleLogoRemove = () => {
    updateVisualStyle({
      logoImage: undefined,
      ...(visualStyle.brandId ? { brandId: null, brandContext: null } : {}),
    });
  };

  const handleMainPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMainPrice(value);
    updateProductData({ ...productData, price: value });
    if (priceErrors.main) setPriceErrors({ ...priceErrors, main: undefined });
  };

  const handleCountryChange = (countryCode: string) => {
    const selectedCountry = countryOptions.find(option => option.country === countryCode);
    updateProductData({
      ...productData,
      countryCode,
      currencyCode: selectedCountry?.currency ?? productData.currencyCode ?? Currency.COP,
    });
    setIsCountryMenuOpen(false);
  };

  const handleBumpOfferPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBumpOfferPrice(value);

    if (data.additionalOffers.bumpOffer) {
      updateAdditionalOffers({
        ...data.additionalOffers,
        bumpOffer: {
          ...data.additionalOffers.bumpOffer,
          price: value
        }
      });
    }
    if (priceErrors.bump) setPriceErrors({ ...priceErrors, bump: undefined });
  };

  const mainPreviousProductChanged = hasPreviousProductOfferChange(
    productData.previousProductSnapshot,
    mainPrice,
    accountCountry,
    accountCurrency
  );
  const bumpPreviousProductChanged = hasPreviousProductOfferChange(
    data.additionalOffers.bumpOffer?.previousProductSnapshot,
    bumpOfferPrice,
    accountCountry,
    accountCurrency
  );
  const mainPriceWidthCh = Math.min(13, Math.max(7, mainPrice.length + 1));
  const configuredVariantsCount = productData.variants?.length ?? 0;
  const productVariantImages = data.visualContent.images.length > 0
    ? data.visualContent.images
    : data.visualContent.imagesBase64;

  useEffect(() => {
    const nextWarranty = productData.warranty || '30days';
    const selectedCountry = countryOptions.find(option => option.country === accountCountry);
    setWarrantyEnabled(nextWarranty !== 'none');
    updateProductData({
      ...productData,
      countryCode: accountCountry,
      currencyCode: accountCurrency || selectedCountry?.currency || Currency.COP,
      warranty: nextWarranty,
      customWarrantyDays: nextWarranty === 'none' ? undefined : productData.customWarrantyDays || '30'
    });
  }, []);

  useEffect(() => {
    fetchSectionTemplates();
    fetchAdTemplates();
  }, [fetchSectionTemplates, fetchAdTemplates]);

  useEffect(() => {
    const canRankPages = !isFetchingTemplates && sectionTemplates.length > 0;
    const canRankAds = !isFetchingAdTemplates && adTemplates.length > 0;
    if (canRankPages || canRankAds) {
      rankOnboardingTemplates();
    }
  }, [isFetchingTemplates, isFetchingAdTemplates, sectionTemplates.length, adTemplates.length, rankOnboardingTemplates]);

  const handleWarrantyToggle = (enabled: boolean) => {
    setWarrantyEnabled(enabled);
    updateProductData({
      ...productData,
      warranty: enabled ? '30days' : 'none',
      customWarrantyDays: enabled ? '30' : undefined
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {dictionary["brand-price-warranty"]?.[language] ?? "Brand, price and warranty"}
        </h1>
      </div>

      <div className="max-w-2xl mx-auto space-y-3">

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label className="block font-extrabold text-sm text-[#1E293B]">
              {dictionary["brand-name"][language]}
            </label>
            <button
              type="button"
              onClick={handleGenerateBrandName}
              disabled={isGenerating}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3B4A9F] transition-colors hover:text-[#263581] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isGenerating ? <Loader size={14} /> : <Sparkles className="h-3.5 w-3.5" />}
              {dictionary["generate-with-ai"][language]}
            </button>
          </div>
          <div className="flex gap-3">
            <input
              type="text"
              autoFocus
              value={brandName}
              disabled={isGenerating}
              onChange={handleBrandNameChange}
              placeholder={dictionary["enter-your-brand-name-placeholder"][language]}
              className="h-11 min-w-0 flex-1 rounded-xl border border-[#E2E8F0] px-4 text-sm text-[#1E293B] transition focus:border-[#3B4A9F] focus:outline-none focus:ring-0 focus:shadow-[inset_0_0_0_1px_#3B4A9F] disabled:bg-[#F8FAFC]"
            />
            {!previewLogo && (
              <label className="inline-flex h-11 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#F8FAFC] px-4 text-xs font-semibold text-[#475569] ring-1 ring-[#E2E8F0] transition-colors hover:bg-gray-100">
                <Upload className="h-3.5 w-3.5" />
                Subir logo <span className="font-medium text-[#94A3B8]">(Opcional)</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => void handleLogoUpload(event.target.files?.[0])}
                />
              </label>
            )}
          </div>
          {brandGenerationError && (
            <p className="mt-2 text-xs text-red-500">
              {brandGenerationError}
            </p>
          )}
        </div>

        {previewLogo && (
          <div className="space-y-2">
            <span className="flex items-center gap-2 font-extrabold text-sm text-[#1E293B]">
              <ImageIcon className="h-4 w-4 text-[#64748B]" />
              Logo
            </span>
            <div className="rounded-[18px] border border-[#E2E8F0] bg-[#FCFCFD] p-3">
              <div className="flex min-h-[92px] items-center justify-center rounded-2xl border border-dashed border-[#CBD5E1] bg-white px-5 py-4">
                <img src={previewLogo} alt="Brand logo" className="max-h-16 w-full object-contain" />
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <label className="inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-xl bg-white px-3 text-xs font-semibold text-[#475569] ring-1 ring-[#E2E8F0] transition-colors hover:bg-gray-50">
                  <Upload className="h-3.5 w-3.5" />
                  Cambiar
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => void handleLogoUpload(event.target.files?.[0])}
                  />
                </label>
                <button
                  type="button"
                  onClick={handleLogoRemove}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-xl bg-white px-3 text-xs font-semibold text-[#64748B] ring-1 ring-[#E2E8F0] transition-colors hover:bg-gray-50 hover:text-[#1E293B]"
                >
                  <X className="h-3.5 w-3.5" />
                  Quitar
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-[#FCFCFD] rounded-[18px] border border-[#E2E8F0] p-2">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
              {data.visualContent.imagesBase64[0] || data.visualContent.images[0] ? (
                <img src={data.visualContent.imagesBase64[0] || data.visualContent.images[0]} alt={productData.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-gray-900 truncate">
                {productData.name || dictionary["main-product"][language]}
              </h3>
              <span className="inline-block mt-1 px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] text-xs font-medium rounded">
                {dictionary["main-product"][language]}
              </span>
            </div>
            <div className="relative shrink-0">
              <div className={`flex h-11 items-center rounded-xl border bg-white transition focus-within:ring-1 ${
                priceErrors.main
                  ? 'border-red-500 focus-within:border-red-500 focus-within:ring-red-500'
                  : 'border-gray-300 focus-within:border-[#3B4A9F] focus-within:ring-[#3B4A9F]'
              }`}>
                <span className="shrink-0 pl-3 text-sm text-gray-500">{currencySymbol}</span>
                <input
                  type="number"
                  value={mainPrice}
                  onChange={handleMainPriceChange}
                  placeholder="50000"
                  style={{ width: `${mainPriceWidthCh}ch` }}
                  className="min-w-0 bg-transparent px-1.5 text-right text-sm outline-none placeholder:text-gray-300 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <button
                  type="button"
                  onClick={() => setIsCountryMenuOpen((isOpen) => !isOpen)}
                  className="mr-1 inline-flex h-8 shrink-0 items-center gap-1 rounded-lg px-2 text-[10px] font-bold uppercase text-gray-500 transition-colors hover:bg-[#F1F5F9]"
                >
                  {accountCurrency}
                  <ChevronDown className={`h-3.5 w-3.5 text-[#64748B] transition-transform ${isCountryMenuOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>
              {isCountryMenuOpen && (
                <div className="absolute bottom-[calc(100%+0.5rem)] right-0 z-[80] w-64 max-h-44 overflow-y-auto rounded-2xl border border-[#DDE2F1] bg-white p-1.5 shadow-[0_18px_45px_rgba(30,41,59,0.18)]">
                  {countryOptions.map(option => {
                    const isSelected = option.country === accountCountry;

                    return (
                      <button
                        type="button"
                        key={option.country}
                        onClick={() => handleCountryChange(option.country)}
                        className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold transition-colors ${
                          isSelected
                            ? 'bg-[#EEF3FF] text-[#3B4A9F]'
                            : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#1E293B]'
                        }`}
                      >
                        <span className="min-w-0 truncate">{option.flag} {option.name}</span>
                        <span className="shrink-0 text-xs font-bold text-[#94A3B8]">{option.currency}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {priceErrors.main && (
                <p className="absolute right-0 top-[calc(100%+0.25rem)] text-xs text-red-500">
                  {priceErrors.main}
                </p>
              )}
            </div>
          </div>
          {mainPreviousProductChanged && (
            <p className="mt-2 rounded-lg bg-blue-50 px-2 py-1.5 text-xs text-blue-700">
              {previousProductOfferMessage}
            </p>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              setIsCountryMenuOpen(false);
              setIsVariationsPanelOpen((isOpen) => !isOpen);
            }}
            className="inline-flex items-center gap-2 rounded-xl px-2 py-1.5 text-sm font-semibold text-[#3B4A9F] transition-colors hover:bg-[#EEF3FF]"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Configurar variaciones
            {configuredVariantsCount > 0 && (
              <span className="rounded-full bg-[#EEF3FF] px-2 py-0.5 text-xs font-extrabold text-[#3B4A9F]">
                {configuredVariantsCount}
              </span>
            )}
          </button>
        </div>

        {isVariationsPanelOpen && (
          <ProductVariantsConfigurator
            productData={productData}
            productImages={productVariantImages}
            basePrice={Number(mainPrice) || 0}
            currencySymbol={currencySymbol}
            currencyCode={accountCurrency}
            onChange={updateProductData}
            onClose={() => setIsVariationsPanelOpen(false)}
          />
        )}

        {data.additionalOffers.bumpOffer?.enabled && data.additionalOffers.bumpOffer?.productName && (
          <div className="bg-[#F6FFFA] rounded-[18px] border border-[#E3FBEB] p-2">
            <div className={`flex gap-4 ${priceErrors.bump ? 'items-start' : 'items-center'}`}>
              <div className={`w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden ${priceErrors.bump ? 'mt-1' : ''}`}>
                {data.additionalOffers.bumpOffer.url_images || data.additionalOffers.bumpOffer.image ? (
                  <img src={data.additionalOffers.bumpOffer.url_images || data.additionalOffers.bumpOffer.image} alt={data.additionalOffers.bumpOffer.productName} className="w-full h-full object-cover border-[1px] border-[#E3FBEB] rounded-xl" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-green-100 to-green-200" />
                )}
              </div>
              <div className={`flex-1 min-w-0 ${priceErrors.bump ? 'mt-1' : ''}`}>
                <h3 className="font-medium text-[#15803D] truncate">
                  {data.additionalOffers.bumpOffer.productName || dictionary["order-bump"][language]}
                </h3>
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-[#E3FBEB] text-[#15803D] text-xs font-medium rounded">
                  <Gift className="w-4 h-4 text-green-800" />
                  {dictionary["order-bump"][language]}
                </span>
              </div>
              <div className="w-36 flex flex-col items-stretch gap-1">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{currencySymbol}</span>
                  <input
                    type="number"
                    value={bumpOfferPrice}
                    onChange={handleBumpOfferPriceChange}
                    placeholder="12000"
                    className={`w-full pl-7 pr-14 py-2 border rounded-lg focus:outline-none focus:ring-1 text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                      priceErrors.bump
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-[#3B4A9F] focus:ring-[#3B4A9F]'
                    }`}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                    {accountCurrency}
                  </span>
                </div>
                {bumpPreviousProductChanged && (
                  <p className="max-w-xs rounded-lg bg-blue-50 px-2 py-1.5 text-xs text-blue-700">
                    {previousProductOfferMessage}
                  </p>
                )}
                {priceErrors.bump && (
                  <p className="text-xs text-red-500 text-right">
                    {priceErrors.bump}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {data.additionalOffers.upsell?.enabled && data.additionalOffers.upsell.products.map((upsell) => (
          <div key={upsell.id} className="bg-[#FBFAFF] rounded-[18px] border border-[#EFECFF] p-2">
            <div className={`flex gap-4 ${priceErrors.upsell ? 'items-start' : 'items-center'}`}>
              <div className={`w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden ${priceErrors.upsell ? 'mt-1' : ''}`}>
                {upsell.url_images?.[0] || upsell.image ? (
                  <img src={upsell.url_images?.[0] || upsell.image || ''} alt={upsell.productName} className="w-full h-full object-cover border-[1px] border-[#EFECFF] rounded-xl" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-purple-100 to-purple-200" />
                )}
              </div>
              <div className={`flex-1 min-w-0 ${priceErrors.upsell ? 'mt-1' : ''}`}>
                <h3 className="font-medium text-[#5B20B6] truncate">
                  {upsell.productName || `${dictionary["upsell-title"][language]}`}
                </h3>
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-[#EDE9FE] text-[#5B20B6] text-xs font-medium rounded">
                  <ShoppingBag className="w-4 h-4 text-[#6D29D9]" />
                  {dictionary["upsell-title"][language]}
                </span>
              </div>
              <div className="w-36 flex flex-col items-stretch gap-1">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{currencySymbol}</span>
                  <input
                    type="number"
                    value={upsell.price}
                    onChange={(e) => {
                      const updatedProducts = data.additionalOffers.upsell.products.map(p =>
                        p.id === upsell.id ? { ...p, price: e.target.value } : p
                      );
                      updateAdditionalOffers({
                        ...data.additionalOffers,
                        upsell: {
                          ...data.additionalOffers.upsell,
                          products: updatedProducts
                        }
                      });
                      if (priceErrors.upsell) setPriceErrors({ ...priceErrors, upsell: undefined });
                    }}
                    placeholder="25000"
                    className={`w-full pl-7 pr-14 py-2 border rounded-lg focus:outline-none focus:ring-1 text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                      priceErrors.upsell
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-[#3B4A9F] focus:ring-[#3B4A9F]'
                    }`}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                    {accountCurrency}
                  </span>
                </div>
                {hasPreviousProductOfferChange(
                  upsell.previousProductSnapshot,
                  upsell.price,
                  accountCountry,
                  accountCurrency
                ) && (
                  <p className="max-w-xs rounded-lg bg-blue-50 px-2 py-1.5 text-xs text-blue-700">
                    {previousProductOfferMessage}
                  </p>
                )}
                {priceErrors.upsell && (
                  <p className="text-xs text-red-500 text-right">
                    {priceErrors.upsell}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}

        <div
          className="rounded-[16px] px-4 py-3 mt-6 border border-gray-200 bg-[#FCFCFD] flex justify-between items-center transition-all duration-200"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 shadow-[0_0_10px_rgba(0,0,0,0.08)]">
              <Shield className="w-5 h-5 text-gray-500" />
            </div>

            <div className="flex items-center gap-1.5">
              <span className="font-medium text-gray-900">
                {dictionary["warranty"][language]}
              </span>
              {/* Fix #3 (2026-05-19): tooltip explaining the warranty is announced on the sales page. */}
              <Tooltip content={dictionary["warranty-tooltip"][language]}>
                <button
                  type="button"
                  aria-label={dictionary["warranty-tooltip"][language]}
                  className="inline-flex h-4 w-4 items-center justify-center text-gray-400 cursor-help focus:outline-none focus:ring-2 focus:ring-blue-200 rounded-full"
                >
                  <Info className="w-4 h-4" />
                </button>
              </Tooltip>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {warrantyEnabled && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">{dictionary["days"][language]}:</span>
                <input
                  type="number"
                  value={productData.customWarrantyDays ?? ''}
                  onChange={(e) => {
                    updateProductData({
                      ...productData,
                      warranty: 'custom',
                      customWarrantyDays: e.target.value
                    });
                  }}
                  className="w-20 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-right text-sm focus:outline-none focus:border-gray-400"
                />
              </div>
            )}

            <Toggle enabled={warrantyEnabled} onChange={handleWarrantyToggle} />
          </div>
        </div>

      </div>
    </div>
  );
}
